import { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from 'shared-ui';

import {
  fetchDodDlqSummary,
  fetchDodOverview,
  fetchDodQueues,
  fetchDodTimeseries,
  fetchDodWorker,
} from 'api/dod/dod.v2';
import {
  DOD_OVERVIEW_METRICS,
  DOD_PAGE_SIZE_MAX,
  DodDlqSummaryResponse,
  DodOverviewResponse,
  DodQueueSummaryV2,
  DodQueuesResponse,
  DodTimeseriesLabelName,
  DodRange,
  DodTimeseriesResponse,
  DodWorkerResponse,
} from 'api/dod/dod.v2.types';
import { QueryKeyEnum } from 'core/global.enum';
import { rangeStepSeconds } from '../hooks/useDodFilters/useDodFilters';
import { DodRangePreset, UseDodFiltersResult } from '../hooks/useDodFilters/useDodFilters.types';

/**
 * Every query the Przeglad tab needs, keyed consistently on range + filters + the shell's manual
 * refresh token, and polled at the interval the Grafana bar selects.
 *
 * `fromTs`/`toTs` slide on every render for a preset range, so they are deliberately kept out of
 * the query keys (see `useDodFilters`); the window is resolved inside this hook at call time and
 * the key carries the preset instead.
 */

/** The backend's DodRange has no 'custom'; a custom window falls back to the 1h aggregates. */
export const toDodRange = (preset: DodRangePreset): DodRange => (preset === 'custom' ? '1h' : preset);

export type OverviewSeriesQuery = {
  data?: DodTimeseriesResponse;
  isLoading: boolean;
  /** The request failed; the panel shows its own error state (queries never toast). */
  error?: unknown;
};

type SeriesContext = {
  from: number;
  to: number;
  step: number;
  rcpId?: string;
  nlk?: string;
  command?: string;
  refetchInterval: number | false;
  /** Key segments shared by every query on the tab: range, filters and the refresh token. */
  keyBase: Array<string | number>;
};

/**
 * One rollup series.
 *
 * `groupBy` is an array of label names; shared-ui serialises args with `arrayFormat: 'comma'`,
 * which is the comma-separated list the route parses. An unknown name is a 400, so the type is
 * narrowed to the four dimensions a rollup field actually carries.
 */
const useDodSeries = (
  metric: string,
  context: SeriesContext,
  groupBy?: DodTimeseriesLabelName[],
): OverviewSeriesQuery => {
  const { data, isLoading, error } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, metric, groupBy?.join(',') ?? '', ...context.keyBase],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: {
        metric,
        from: context.from,
        to: context.to,
        step: context.step,
        rcpId: context.rcpId,
        nlk: context.nlk,
        command: context.command,
        groupBy,
      },
      refetchInterval: context.refetchInterval,
      keepPreviousData: true,
    },
  );

  return { data, isLoading, error: error ?? undefined };
};

/**
 * Reads every queue the fleet has, as a bounded walk over `/dod/queues`.
 *
 * `pageSize` is clamped to 200 server-side, so one request cannot hold a large fleet. React's
 * hook order has to be fixed, so the walk is a constant number of queries: each page is fetched
 * only once the previous page has reported a `total` that reaches it, and the result says whether
 * the cap was hit, so the UI can admit to showing a subset instead of implying it has them all.
 */
const HEATMAP_MAX_PAGES = 5;

type QueuePagesArgs = {
  rcpId?: string;
  refetchInterval: number | false;
  refreshToken: number;
};

export type QueuePagesResult = {
  items: DodQueueSummaryV2[];
  /** Matches the API reports across the fleet, which may exceed what the walk could load. */
  total: number;
  isLoading: boolean;
  /** True when the fleet is larger than the walk's ceiling; the UI must say so. */
  isTruncated: boolean;
};

const useQueuePage = (page: number, isEnabled: boolean, args: QueuePagesArgs): UseQueryResult<DodQueuesResponse> =>
  useQuery([QueryKeyEnum.DOD_QUEUES, 'heatmap', page, args.rcpId, args.refreshToken], fetchDodQueues, {
    handleErrorManually: true,
    args: { page, pageSize: DOD_PAGE_SIZE_MAX, rcpId: args.rcpId },
    enabled: isEnabled,
    refetchInterval: args.refetchInterval,
    keepPreviousData: true,
  });

const useQueuePages = (args: QueuePagesArgs): QueuePagesResult => {
  const first = useQueuePage(1, true, args);
  const total = first.data?.total ?? 0;

  const needsPage = (page: number): boolean => total > (page - 1) * DOD_PAGE_SIZE_MAX;

  const second = useQueuePage(2, needsPage(2), args);
  const third = useQueuePage(3, needsPage(3), args);
  const fourth = useQueuePage(4, needsPage(4), args);
  const fifth = useQueuePage(5, needsPage(5), args);

  const pages = [first, second, third, fourth, fifth];

  return {
    items: pages.flatMap((page) => page.data?.items ?? []),
    total,
    isLoading: pages.some((page) => page.isLoading && page.fetchStatus !== 'idle'),
    isTruncated: total > HEATMAP_MAX_PAGES * DOD_PAGE_SIZE_MAX,
  };
};

export type UseOverviewDataResult = {
  overview?: DodOverviewResponse;
  isOverviewLoading: boolean;
  isOverviewError: boolean;
  enqueued: OverviewSeriesQuery;
  delivered: OverviewSeriesQuery;
  failed: OverviewSeriesQuery;
  deferred: OverviewSeriesQuery;
  queueDepth: OverviewSeriesQuery;
  dlqSize: OverviewSeriesQuery;
  outbox: OverviewSeriesQuery;
  dlqSummary?: DodDlqSummaryResponse;
  isDlqSummaryLoading: boolean;
  /** Every queue the walk could load, plus the fleet total and whether it was capped. */
  heatmapPages: QueuePagesResult;
  worker?: DodWorkerResponse;
  fromTs: number;
  toTs: number;
  stepMs: number;
};

type UseOverviewDataArgs = {
  filters: UseDodFiltersResult;
  refreshToken: number;
};

export const useOverviewData = ({ filters, refreshToken }: UseOverviewDataArgs): UseOverviewDataResult => {
  const { range, refreshMs, filters: selected, resolveRange } = filters;
  const { fromTs, toTs } = resolveRange();
  const stepMs = rangeStepSeconds(range) * 1000;
  const dodRange = toDodRange(range);

  const seriesKeyBase = [range, selected.rcp, selected.nlk, selected.cmd, refreshToken];

  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
  } = useQuery([QueryKeyEnum.DOD_OVERVIEW, ...seriesKeyBase], fetchDodOverview, {
    handleErrorManually: true,
    args: { range: dodRange },
    refetchInterval: refreshMs,
  });

  const seriesContext: SeriesContext = {
    from: fromTs,
    to: toTs,
    step: stepMs,
    rcpId: selected.rcp || undefined,
    nlk: selected.nlk || undefined,
    command: selected.cmd || undefined,
    refetchInterval: refreshMs,
    keyBase: seriesKeyBase,
  };

  const enqueued = useDodSeries(DOD_OVERVIEW_METRICS.enqueued, seriesContext);
  const delivered = useDodSeries(DOD_OVERVIEW_METRICS.delivered, seriesContext);
  const failed = useDodSeries(DOD_OVERVIEW_METRICS.failed, seriesContext);
  const deferred = useDodSeries(DOD_OVERVIEW_METRICS.deferred, seriesContext);
  const queueDepth = useDodSeries(DOD_OVERVIEW_METRICS.queueDepth, seriesContext, ['nlk']);
  const dlqSize = useDodSeries(DOD_OVERVIEW_METRICS.dlqSize, seriesContext);
  const outbox = useDodSeries(DOD_OVERVIEW_METRICS.outboxOrders, seriesContext);

  const { data: dlqSummary, isLoading: isDlqSummaryLoading } = useQuery(
    [QueryKeyEnum.DOD_DLQ_SUMMARY, ...seriesKeyBase],
    fetchDodDlqSummary,
    {
      handleErrorManually: true,
      args: { range: dodRange, rcpId: selected.rcp || undefined },
      refetchInterval: refreshMs,
    },
  );

  /**
   * The heatmap's source. The overview response carries no per-TKD data, so the tab reads the
   * queue list and lays it out itself, walking pages because `pageSize` is clamped to 200 and a
   * larger fleet would otherwise truncate with nothing on screen saying so.
   */
  const heatmapPages = useQueuePages({
    rcpId: selected.rcp || undefined,
    refetchInterval: refreshMs,
    refreshToken,
  });

  /** Connection events become the vertical annotations on the throughput chart. */
  const { data: worker } = useQuery([QueryKeyEnum.DOD_WORKER, 'events', refreshToken], fetchDodWorker, {
    handleErrorManually: true,
    args: { limit: 50 },
    refetchInterval: refreshMs,
    retry: false,
  });

  return {
    overview,
    isOverviewLoading,
    isOverviewError,
    enqueued,
    delivered,
    failed,
    deferred,
    queueDepth,
    dlqSize,
    outbox,
    dlqSummary,
    isDlqSummaryLoading,
    heatmapPages,
    worker,
    fromTs,
    toTs,
    stepMs,
  };
};
